/**
 * 환경 프리팹(SR-PHY-04 부분 · ARC §10) — 배치(미터, 모드 정의) → 도형·자세(미터). 커널이 cm 로 바꿔 D 월드에 놓는다.
 *
 * | 프리팹 | at 의 뜻 | 도형 | 기본 표면 |
 * |---|---|---|---|
 * | platform · start-pad · bridge | 윗면 중심 | 상자 size | ground · start · bridge |
 * | step · wall | 밑면 중심 | 상자 size | step · wall |
 * | ramp | 밑면 중심 | 쐐기: 가로 size x · 높이 size y · 길이 size z — 지역 −z 쪽이 낮고 +z 쪽이 높다 | ramp |
 * | finish-zone | 밑면 중심 | 트리거 부피 size(충돌 없음 — SR-PHY-04 존) | finish |
 * | pedestal | 밑면 중심 | 상자 size — 전시관 받침대(S4 · 모드 정의 gallery 에서 커널이 만든다) | pedestal |
 * | crate | 밑면 중심 | 상자 size — mass 가 있으면 동적 물체(P2-S3) | crate |
 * | ball | 밑면(닿는 점) | 구: 지름 = size x(size = [d, d, d]) — mass 가 있으면 동적 물체(P2-S5 자유 놀이터) | ball |
 * | lever | 밑면 중심 | 감지 상자 size(충돌 없음 · {@link EnvPiece.sensor}) 가운데에 고정 손잡이 상자({@link LEVER_HANDLE}) — P3-S1 릴레이 | lever |
 * | conveyor | 윗면 중심 | 상자 size — 제자리 벨트: 표면이 speed(m/s · 지역 +z)로 흐른다({@link EnvPiece.motion} belt) — S4 | conveyor |
 * | line | 밑면 중심 | 얇은 상자 size — 바닥에 붙인 검은 선 테이프(선 따라가기 판 · 색 센서 검정 0 · 반사 10 · 2026-10-06) | line |
 * | moving-platform | 윗면 중심(위상 0 자리) | 상자 size — at ↔ at + travel 을 periodS 초에 한 번 왕복(틱으로만 정한다 · {@link moverOffset}) — S4 | mover |
 *
 * 바디 원점 = 도형 바운딩 상자 중심(렌더러가 도형을 원점에 그리고 프레임 자세를 곱한다).
 * yaw 는 90° 단위라 회전은 정확한 표에서 온다(초월함수 없음 — 결정론).
 */
import { atan2 } from "@picnic/mech-kinematics";
import type { EnvPlacement, ModeDefinitionV1, PrefabKind, Vec3, Yaw } from "./types";

export type Quat = readonly [number, number, number, number];

export interface PrefabInfo {
  /** env = 고정 · zone = 트리거 · object = 동적 물체(질량) */
  readonly kind: "env" | "zone" | "object";
  readonly surface: string;
  /** at 이 윗면(top) · 밑면(bottom) 중심 */
  readonly anchor: "top" | "bottom";
  readonly needs: readonly "size"[];
}

export const PREFABS: Readonly<Record<PrefabKind, PrefabInfo>> = {
  platform: { kind: "env", surface: "ground", anchor: "top", needs: ["size"] },
  "start-pad": { kind: "env", surface: "start", anchor: "top", needs: ["size"] },
  bridge: { kind: "env", surface: "bridge", anchor: "top", needs: ["size"] },
  step: { kind: "env", surface: "step", anchor: "bottom", needs: ["size"] },
  wall: { kind: "env", surface: "wall", anchor: "bottom", needs: ["size"] },
  ramp: { kind: "env", surface: "ramp", anchor: "bottom", needs: ["size"] },
  "finish-zone": { kind: "zone", surface: "finish", anchor: "bottom", needs: ["size"] },
  pedestal: { kind: "env", surface: "pedestal", anchor: "bottom", needs: ["size"] },
  line: { kind: "env", surface: "line", anchor: "bottom", needs: ["size"] },
  // P2-S3 — 스모 링(윗면 중심 · size x = 지름 · y = 높이 · 16각 기둥 헐) · 협동 목표 존 · 운반 물체
  ring: { kind: "env", surface: "ring", anchor: "top", needs: ["size"] },
  "goal-zone": { kind: "zone", surface: "goal", anchor: "bottom", needs: ["size"] },
  crate: { kind: "object", surface: "crate", anchor: "bottom", needs: ["size"] },
  // P2-S5 — 자유 놀이터 공(구 콜라이더 · 동적)
  ball: { kind: "object", surface: "ball", anchor: "bottom", needs: ["size"] },
  // P3-S1 릴레이 레버 — 물리는 손잡이 상자(고정 · 기계가 닿으면 contact) · 감지 상자는 조회용(물리 없음)
  lever: { kind: "env", surface: "lever", anchor: "bottom", needs: ["size"] },
  // S4(2026-09-28) SR-PHY-04 — 컨베이어 · 이동 발판(둘 다 kinematic 환경 · 바닥류라 윗면 중심)
  conveyor: { kind: "env", surface: "conveyor", anchor: "top", needs: ["size"] },
  "moving-platform": { kind: "env", surface: "mover", anchor: "top", needs: ["size"] },
};

/** 컨베이어 기본 벨트 속도(m/s) · 기본 마찰(벨트가 잡아 끈다) */
export const CONVEYOR_DEFAULT_SPEED = 0.3;
export const CONVEYOR_FRICTION = 1.0;
/** 이동 발판 기본 왕복 주기(초) · 주기 범위(스키마와 같다) */
export const MOVER_DEFAULT_PERIOD_S = 6;
export const MOVER_PERIOD_MIN_S = 1;
export const MOVER_PERIOD_MAX_S = 120;
/** 60 Hz 틱(커널 TICK_HZ 와 같다) — 주기 · 위상은 정수 틱으로 바꿔 둔다(결정론) */
const TICKS_PER_S = 60;

/**
 * S4 움직이는 환경(SR-PHY-04) — 모두 **틱 수만의 함수**(벽시계 없음 · 사칙연산만):
 *  - belt: 몸체는 제자리 · 서브스텝마다 원점에서 velocity × dt 만큼 가는 kinematic 목표를 준다 → 솔버가 표면 속도로 보고 마찰로 끈다
 *  - path: 원점(위상 0 자리) + travel × {@link moverOffset}(t) — t = 틱(서브스텝은 소수 틱)
 */
export type EnvMotion =
  | { readonly type: "belt"; readonly velocity: Vec3 }
  | {
      readonly type: "path";
      readonly travel: Vec3;
      readonly periodTicks: number;
      readonly phaseTicks: number;
    };

/**
 * 이동 발판 위치 비율(0 = 원점 · 1 = 원점 + travel) — 삼각파(가서 돌아옴)를 smoothstep(3s² − 2s³)으로 부드럽게.
 * 양 끝에서 속도 0(위에 탄 것이 튀지 않게) · 초월함수 없음 · t 는 틱(소수 가능 — 서브스텝 끝 시각).
 */
export function moverOffset(t: number, periodTicks: number, phaseTicks = 0): number {
  const p = periodTicks > 0 ? periodTicks : 1;
  let u = (t + phaseTicks) % p;
  if (u < 0) u += p;
  u /= p;
  const s = u < 0.5 ? 2 * u : 2 - 2 * u;
  return s * s * (3 - 2 * s);
}

/** 지역 벡터를 yaw(90° 단위)로 돌린다 — 정확한 표(YAW_QUAT 과 같은 회전: +z → yaw 90 에서 +x) */
export function yawVec(v: Vec3, yaw: Yaw): Vec3 {
  const [x, y, z] = v;
  // 0 을 더해 −0 을 없앤다(해시 · JSON 이 같은 값을 쓰게)
  if (yaw === 90) return [z + 0, y, -x + 0];
  if (yaw === 180) return [-x + 0, y, -z + 0];
  if (yaw === 270) return [-z + 0, y, x + 0];
  return [x, y, z];
}

/**
 * P3-S1 레버 손잡이(지역 m · [흐름 x · 높이 y · 폭 z]) — 감지 상자(size)보다 크면 감지 상자에 맞춰 줄인다.
 * 물리 관절 없음: 움직이지 않는 고정 콜라이더(속도 0 의 kinematic 과 같다). 눌림 = 기계 콜라이더가 닿음(ModeEngine contact 와 같은 뜻) —
 * 넘어가는 모습은 렌더러만 그린다(FIELD-DEC-003 §3.3). 높이 0.12 m 는 대표 주행 모형 차체(≈ 0.08 m)의 앞면이 닿는 높이다.
 */
export const LEVER_HANDLE: Vec3 = [0.1, 0.12, 0.5];

/**
 * 표면 → SPIKE 컬러 센서 값(SR-RUN-03 color = 표면 재질 색 샘플). 색 번호는 SPIKE Prime 규약
 * (0 검정 · 1 자홍 · 3 파랑 · 4 하늘 · 6 초록 · 7 노랑 · 8 주황 · 9 빨강 · 10 흰색 · −1 없음) · 반사광 %.
 * 셸이 표면 색을 고를 때도 이 표를 쓴다(렌더 색 = 센서 색).
 */
export const SURFACE_SENSOR: Readonly<Record<string, { color: number; reflection: number }>> = {
  ground: { color: 10, reflection: 80 },
  start: { color: 3, reflection: 30 },
  finish: { color: 9, reflection: 45 },
  ramp: { color: 7, reflection: 70 },
  step: { color: 8, reflection: 55 },
  bridge: { color: 6, reflection: 40 },
  wall: { color: 0, reflection: 8 },
  ice: { color: 4, reflection: 85 },
  pedestal: { color: 10, reflection: 90 },
  // 선 따라가기 판 — 검은 선 테이프(SPIKE 색 0 = 검정 · 반사 10 — 흰 바닥 80 과 문턱 50 으로 갈린다)
  line: { color: 0, reflection: 10 },
  // 걷기 판(2026-10-06) — 초록 잔디 길(SPIKE 색 6 = 초록 · 거북이 정답: 초록이면 걷고 빨강(결승 9)이면 멈춘다)
  grass: { color: 6, reflection: 40 },
  ring: { color: 10, reflection: 75 },
  goal: { color: 6, reflection: 40 },
  crate: { color: 8, reflection: 50 },
  ball: { color: 1, reflection: 35 },
  lever: { color: 7, reflection: 60 },
  // S4 — 컨베이어(검은 고무 벨트) · 이동 발판(주황)
  conveyor: { color: 0, reflection: 15 },
  mover: { color: 8, reflection: 55 },
};
export const NO_SURFACE = { color: -1, reflection: 0 } as const;

export type PieceShape =
  | {
      readonly type: "box";
      readonly half: Vec3;
      /**
       * S4 렌더 장식(선택 · 물리와 무관): belt = 컨베이어(speed = 벨트 m/s · 지역 +z 가 +) · mover = 이동 발판.
       * 없으면 그냥 상자 — 기존 프리팹은 이 키를 싣지 않는다(환경 해시 불변).
       */
      readonly deco?: "belt" | "mover";
      readonly speed?: number;
    }
  | {
      readonly type: "cylinder";
      readonly half: Vec3;
      readonly radius: number;
      readonly halfHeight: number;
    }
  | { readonly type: "ramp"; readonly half: Vec3; readonly angleDeg: number }
  /** P2-S5 ball — 반지름(m) · half = [r, r, r] */
  | { readonly type: "sphere"; readonly half: Vec3; readonly radius: number };

/** 펼친 환경 한 조각(미터) */
export interface EnvPiece {
  readonly id: string;
  readonly prefab: PrefabKind;
  readonly kind: "env" | "zone" | "object";
  readonly surface: string;
  /** 바디 원점(도형 바운딩 상자 중심, 월드 m) */
  readonly position: Vec3;
  readonly yaw: Yaw;
  readonly rotation: Quat;
  readonly shape: PieceShape;
  /** ramp 쐐기 헐(바디 지역 m, 6점) */
  readonly hull?: readonly Vec3[];
  readonly friction?: number;
  /** object — 질량(kg) */
  readonly mass?: number;
  /**
   * P3-S1 lever — 감지 상자(월드 m · 축 정렬 — yaw 를 반영한 반 크기). 물리 없음 · 조회만(구간 물체 중심이 이 안 = 눌림).
   * 몸체(shape · position)는 손잡이다.
   */
  readonly sensor?: { readonly center: Vec3; readonly half: Vec3 };
  /** S4 conveyor · moving-platform — 움직임(월드 m · 틱). position 은 위상 0(원점) 자리 */
  readonly motion?: EnvMotion;
}

const H = Math.SQRT1_2;
/** 링 헐 — 16각 기둥(표 값: 결정론을 위해 초월함수 없이 고정 소수) */
const RING16: readonly (readonly [number, number])[] = [
  [1, 0],
  [0.92388, 0.382683],
  [0.707107, 0.707107],
  [0.382683, 0.92388],
  [0, 1],
  [-0.382683, 0.92388],
  [-0.707107, 0.707107],
  [-0.92388, 0.382683],
  [-1, 0],
  [-0.92388, -0.382683],
  [-0.707107, -0.707107],
  [-0.382683, -0.92388],
  [0, -1],
  [0.382683, -0.92388],
  [0.707107, -0.707107],
  [0.92388, -0.382683],
];
/** y 축 둘레 yaw — 정확한 표 */
export const YAW_QUAT: Readonly<Record<Yaw, Quat>> = {
  0: [0, 0, 0, 1],
  90: [0, H, 0, H],
  180: [0, 1, 0, 0],
  270: [0, -H, 0, H],
};

export function expandPlacement(e: EnvPlacement): EnvPiece {
  const info = PREFABS[e.prefab];
  // ball 은 지름 하나(size x) — 세 축을 같게 둔다
  const size: Vec3 =
    e.prefab === "ball" && e.size ? [e.size[0], e.size[0], e.size[0]] : (e.size ?? [1, 1, 1]);
  const yaw = e.yaw ?? 0;
  const h = size[1];
  const half: Vec3 = [size[0] / 2, h / 2, size[2] / 2];
  const cy = info.anchor === "top" ? e.at[1] - h / 2 : e.at[1] + h / 2;
  const base = {
    id: e.id,
    prefab: e.prefab,
    kind: info.kind,
    surface: e.surface ?? info.surface,
    position: [e.at[0], cy, e.at[2]] as Vec3,
    yaw,
    rotation: YAW_QUAT[yaw],
    ...(e.friction !== undefined ? { friction: e.friction } : {}),
    ...(info.kind === "object" && e.mass !== undefined ? { mass: e.mass } : {}),
  };
  if (e.prefab === "ring") {
    const r = size[0] / 2;
    return {
      ...base,
      shape: { type: "cylinder", half, radius: r, halfHeight: h / 2 },
      hull: RING16.flatMap(([c, s2]) => [
        [c * r, -h / 2, s2 * r] as Vec3,
        [c * r, h / 2, s2 * r] as Vec3,
      ]),
    };
  }
  if (e.prefab === "ball") return { ...base, shape: { type: "sphere", half, radius: size[0] / 2 } };
  if (e.prefab === "conveyor") {
    const speed = e.speed ?? CONVEYOR_DEFAULT_SPEED;
    return {
      ...base,
      ...(e.friction === undefined ? { friction: CONVEYOR_FRICTION } : {}),
      shape: { type: "box", half, deco: "belt", speed },
      motion: { type: "belt", velocity: yawVec([0, 0, speed], yaw) },
    };
  }
  if (e.prefab === "moving-platform") {
    const periodTicks = Math.max(
      2,
      Math.round(
        Math.min(
          MOVER_PERIOD_MAX_S,
          Math.max(MOVER_PERIOD_MIN_S, e.periodS ?? MOVER_DEFAULT_PERIOD_S),
        ) * TICKS_PER_S,
      ),
    );
    const phase = e.phase ?? 0;
    return {
      ...base,
      shape: { type: "box", half, deco: "mover" },
      motion: {
        type: "path",
        travel: yawVec(e.travel ?? [0, 0, 0], yaw),
        periodTicks,
        phaseTicks: Math.round((((phase % 1) + 1) % 1) * periodTicks),
      },
    };
  }
  if (e.prefab === "lever") {
    // 손잡이 — 감지 상자 밑면 가운데에 선다(감지 상자보다 크지 않게)
    const hh: Vec3 = [
      Math.min(LEVER_HANDLE[0], size[0]) / 2,
      Math.min(LEVER_HANDLE[1], size[1]) / 2,
      Math.min(LEVER_HANDLE[2], size[2]) / 2,
    ];
    const swap = yaw === 90 || yaw === 270;
    return {
      ...base,
      position: [e.at[0], e.at[1] + hh[1], e.at[2]],
      shape: { type: "box", half: hh },
      sensor: {
        center: [e.at[0], e.at[1] + h / 2, e.at[2]],
        half: swap ? [half[2], half[1], half[0]] : half,
      },
    };
  }
  if (e.prefab !== "ramp") return { ...base, shape: { type: "box", half } };
  const [x, y, z] = half;
  return {
    ...base,
    // 표시용 각도(물리는 헐 점만 쓴다) — 환경 해시에서는 뺀다(kernel envPiecesForHash). 그래도 엔진마다 같게 결정론 atan2 로 구한다
    // (Math.atan2 는 Chrome↔Node 1ulp 차이 — 4.6 Phase 2 결함 · no-nondeterminism native-math 규칙)
    shape: { type: "ramp", half, angleDeg: (atan2(2 * y, 2 * z) * 180) / Math.PI },
    hull: [
      [-x, -y, -z],
      [x, -y, -z],
      [-x, -y, z],
      [x, -y, z],
      [-x, y, z],
      [x, y, z],
    ],
  };
}

export interface ExpandedCourse {
  readonly presetKey: string;
  readonly pieces: readonly EnvPiece[];
  readonly spawn: { readonly at: Vec3; readonly yaw: Yaw };
  readonly timerS: number;
}

/** 모드 정의 + 프리셋 → 펼친 환경(공통 env 먼저, 배치 순서 = 인스턴스화 순서 = 결정론) */
export function expandCourse(def: ModeDefinitionV1, presetKey: string): ExpandedCourse {
  const p = def.presets.find((x) => x.key === presetKey);
  if (!p) throw new RangeError(`프리셋 없음: ${def.id}/${presetKey}`);
  return {
    presetKey,
    pieces: [...def.env, ...p.env].map(expandPlacement),
    spawn: { at: p.spawn.at, yaw: p.spawn.yaw ?? 0 },
    timerS: p.timerS,
  };
}

/**
 * 전시관 받침대(S4 · SR-MODE-02) — exhibits 수만큼 gallery.pedestals 앞에서부터. id = pedestal-1 …(1부터)
 * 작품 스폰 = 받침대 윗면 중심({@link pedestalTop}).
 */
export function expandPedestals(def: ModeDefinitionV1, count: number): EnvPiece[] {
  const g = def.gallery;
  if (!g) return [];
  return g.pedestals.slice(0, count).map((s, i) =>
    expandPlacement({
      prefab: "pedestal",
      id: `pedestal-${i + 1}`,
      at: s.at,
      ...(s.yaw !== undefined ? { yaw: s.yaw } : {}),
      size: g.pedestal.size,
      ...(g.pedestal.surface ? { surface: g.pedestal.surface } : {}),
    }),
  );
}

/** 받침대 i 윗면 중심 · yaw — 작품이 서는 자리 */
export function pedestalTop(def: ModeDefinitionV1, i: number): { at: Vec3; yaw: Yaw } | null {
  const g = def.gallery;
  const s = g?.pedestals[i];
  if (!g || !s) return null;
  return { at: [s.at[0], s.at[1] + g.pedestal.size[1], s.at[2]], yaw: s.yaw ?? 0 };
}

/** 점(m)이 존(회전 90° 단위 상자) 안에 있나 */
export function insidePiece(piece: EnvPiece, p: Vec3): boolean {
  const [hx, hy, hz] = piece.shape.half;
  const swap = piece.yaw === 90 || piece.yaw === 270;
  const ax = swap ? hz : hx;
  const az = swap ? hx : hz;
  return (
    Math.abs(p[0] - piece.position[0]) <= ax &&
    Math.abs(p[1] - piece.position[1]) <= hy &&
    Math.abs(p[2] - piece.position[2]) <= az
  );
}
